import axios from 'axios'
import type { CollectionFilterQuery } from '../../shared/filters/collectionFilterQuery'
import { appendCollectionFilterParams } from '../../shared/filters/collectionFilterQuery'
import type {
	ClientCollection,
	CollectionEnrichmentResponse,
	CollectionMasterYearUpdate,
	CollectionStatusResponse,
	ReleaseFavoriteResponse,
	SimilarItemsResponse,
	SyncJobSummary,
} from '../../shared/types/collection'
import type {
	ClientDiscogsRelease,
	DiscogsCollectionValue,
	DiscogsFolderResponse,
	ReleaseRatingResponse,
} from '../../shared/types/discogs'
import { delay } from '../utils/async'

type CollectionQuery = CollectionFilterQuery

export async function getCollectionStatus(): Promise<CollectionStatusResponse> {
	const res = await axios.get('/api/collection/status')
	return res.data as CollectionStatusResponse
}

export async function startCollectionSync(
	force = false,
): Promise<SyncJobSummary | null> {
	const res = await axios.post('/api/collection/sync', { force })
	return (res.data?.job as SyncJobSummary | null | undefined) ?? null
}

export async function getSyncJob(
	jobId: string,
): Promise<SyncJobSummary | null> {
	try {
		const res = await axios.get(`/api/jobs/${jobId}`)
		return (res.data?.job as SyncJobSummary | undefined) ?? null
	} catch {
		return null
	}
}

async function waitForSyncJob(
	jobId: string,
	options: { intervalMs?: number; maxAttempts?: number } = {},
): Promise<SyncJobSummary | null> {
	const intervalMs = options.intervalMs ?? 1000
	const maxAttempts = options.maxAttempts ?? 120
	let job: SyncJobSummary | null = null
	for (let i = 0; i < maxAttempts; i++) {
		job = await getSyncJob(jobId)
		if (!job || job.status === 'completed' || job.status === 'failed') {
			return job
		}
		await delay(intervalMs)
	}
	return job
}

/**
 * Fetches one page of collection results using the given filters.
 */
export async function getDiscogsCollectionPage(
	offset: number = 0,
	limit?: number,
	query: CollectionQuery = {},
): Promise<ClientCollection> {
	const params = new URLSearchParams()
	params.set('offset', String(offset))

	if (typeof limit === 'number') {
		params.set('limit', String(limit))
	}

	appendCollectionFilterParams(params, query)

	const res = await axios.get(`/api/collection?${params.toString()}`)
	return res.data?.collection as ClientCollection
}

export async function queueCollectionEnrichment(
	instanceIds: number[],
): Promise<CollectionEnrichmentResponse> {
	const res = await axios.post('/api/collection/enrichment', { instanceIds })
	return res.data as CollectionEnrichmentResponse
}

export async function getCollectionMasterYears(
	instanceIds: number[],
): Promise<CollectionMasterYearUpdate[]> {
	const res = await axios.post('/api/collection/master-years', {
		instanceIds,
	})
	return (res.data?.updates as CollectionMasterYearUpdate[] | undefined) ?? []
}

/**
 * Fetches the user's Discogs folder list.
 */
export async function getDiscogsFolders(): Promise<DiscogsFolderResponse> {
	const res = await axios.get('/api/collection/folders')
	return (
		(res.data as DiscogsFolderResponse | undefined) ?? {
			folders: [],
		}
	)
}

export async function getDiscogsCollectionValue(): Promise<DiscogsCollectionValue> {
	const res = await axios.get('/api/collection/value')
	return res.data?.value as DiscogsCollectionValue
}

export async function saveReleaseRating(
	releaseId: number,
	rating: number | null,
): Promise<ReleaseRatingResponse> {
	const url = `/api/releases/${releaseId}/rating`
	const res =
		rating === null
			? await axios.delete(url)
			: await axios.put(url, { rating })
	return res.data as ReleaseRatingResponse
}

export async function saveReleaseFavorite(
	instanceId: number,
	isFavorite: boolean,
): Promise<ReleaseFavoriteResponse> {
	const res = await axios.put(
		`/api/collection/releases/${instanceId}/favorite`,
		{ isFavorite },
	)
	return res.data as ReleaseFavoriteResponse
}

/**
 * Fetches similar item sections for a release collection instance.
 */
export async function getSimilarReleases(
	instanceId: number,
	options?: { limit?: number },
): Promise<SimilarItemsResponse> {
	const params = new URLSearchParams()
	if (options?.limit) params.set('limit', String(options.limit))
	const query = params.toString()
	const res = await axios.get(
		`/api/collection/releases/${instanceId}/similar${query ? `?${query}` : ''}`,
	)
	return res.data as SimilarItemsResponse
}

/**
 * Fetches full release details for a single release.
 */
export async function getReleaseDetails(
	releaseId: number,
): Promise<ClientDiscogsRelease | null> {
	const readDetailsResponse = (
		responseData: unknown,
	): ClientDiscogsRelease | null => {
		const payload = responseData as {
			details?: ClientDiscogsRelease
		}
		return payload.details ?? null
	}

	try {
		const res = await axios.get(`/api/releases/${releaseId}/details`)
		if (res.status === 202 && res.data?.jobId) {
			const job = await waitForSyncJob(res.data.jobId, {
				maxAttempts: 20,
			})
			if (job?.status === 'completed') {
				const refreshed = await axios.get(
					`/api/releases/${releaseId}/details`,
				)
				return readDetailsResponse(refreshed.data)
			}
			return null
		}
		return readDetailsResponse(res.data)
	} catch (err) {
		console.warn(`Failed to fetch details for release ${releaseId}`, err)
		return null
	}
}
